import type { StateCreator } from 'zustand';
import { CondoStore } from './shared';
import { CondoDocument, Payment } from '../../types';
import { apiService } from '../../services/api';
import { nextRequestToken, isStaleRequest, loadStart, loadSettle, dateUtils } from './shared';

// R10-5 — sum of COMPLETED payments in a window, optionally condo-scoped.
// The real revenue ledger: used as the fallback wherever a report would
// otherwise read the perpetually-empty `revenues` collection (which has no
// backend endpoint or table).
const collectedPaymentsInPeriod = (
  payments: Payment[], start: Date, end: Date, condoId?: string,
): number =>
  payments
    .filter(p => (!condoId || p.condoId === condoId)
      && p.status === 'completed'
      && dateUtils.isWithinInterval(new Date(p.paymentDate), { start, end }))
    .reduce((sum, p) => sum + p.amount, 0);

export const createDocumentsSlice: StateCreator<CondoStore, [], [], Partial<CondoStore>> = (set, get) => ({
  // Document Management with API integration
  fetchDocuments: async (condoId?: string) => {
    const token = nextRequestToken('documents');
    set(loadStart('documents')); // 106.1
    try {
      const documents = await apiService.getDocuments(condoId);
      if (isStaleRequest(token)) return; // 94.4 — superseded by a newer fetch
      set(state => ({ ...loadSettle('documents', null)(state), documents }));
    } catch (error: any) {
      if (isStaleRequest(token)) return;
      set(loadSettle('documents', error.message || 'Failed to fetch documents'));
    }
  },

  uploadDocument: (documentData) => {
    const document: CondoDocument = {
      ...documentData,
      id: `doc-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      uploadDate: new Date()
    };

    set(state => ({ documents: [...state.documents, document] }));
  },

  uploadDocumentApi: async (formData) => {
    const { setLoading, setError, fetchDocuments } = get();
    setLoading(true);
    try {
      await apiService.uploadDocument(formData);
      await fetchDocuments();
    } catch (error: any) {
      setError(error.message || 'Failed to upload document');
    } finally {
      setLoading(false);
    }
  },

  createDocumentApi: async (documentData) => {
    const { setLoading, setError, fetchDocuments } = get();
    setLoading(true);
    try {
      await apiService.createDocument(documentData);
      await fetchDocuments(documentData.condoId);
    } catch (error: any) {
      setError(error.message || 'Failed to create document');
      throw error;
    } finally {
      setLoading(false);
    }
  },

  deleteDocumentApi: async (id) => {
    const { setLoading, setError, documents, fetchDocuments } = get();
    setLoading(true);
    try {
      const existing = documents.find(d => d.id === id);
      await apiService.deleteDocument(id);
      await fetchDocuments(existing?.condoId);
    } catch (error: any) {
      setError(error.message || 'Failed to delete document');
      throw error;
    } finally {
      setLoading(false);
    }
  },

  updateDocument: (id, updatedDocument) =>
    set((state) => ({
      documents: state.documents.map((doc) =>
        doc.id === id ? { ...doc, ...updatedDocument } : doc
      ),
    })),

  deleteDocument: (id) =>
    set((state) => ({
      documents: state.documents.filter((doc) => doc.id !== id),
    })),

  getOwnerDocuments: (ownerId) => {
    return get().documents.filter(doc => String(doc.uploadedBy) === String(ownerId));
  },

  getDocumentsByCategory: (category, condoId?: string) => {
    const { documents } = get();
    const filteredDocs = condoId ? documents.filter(doc => doc.condoId === condoId) : documents;
    return filteredDocs.filter(doc => doc.category === category);
  },

  getPublicDocuments: (condoId?: string) => {
    const { documents } = get();
    const filteredDocs = condoId ? documents.filter(doc => doc.condoId === condoId) : documents;
    // Sprint 36 — finding H3/H7. Jackson strips the `is` prefix on
    // boolean serialisation: the DTO field is `is_public` server-
    // side but arrives as JSON key `public`. The store's
    // `Document.isPublic` shape persists from older code paths.
    // Accept either so seed data + freshly-uploaded docs both work.
    return filteredDocs.filter(doc => doc.public === true || doc.isPublic === true);
  },

  searchDocuments: (query, condoId?: string) => {
    const searchTerm = query.toLowerCase();
    const { documents } = get();
    const filteredDocs = condoId ? documents.filter(doc => doc.condoId === condoId) : documents;

    return filteredDocs.filter(doc =>
      doc.title.toLowerCase().includes(searchTerm) ||
      doc.description.toLowerCase().includes(searchTerm) ||
      doc.category.toLowerCase().includes(searchTerm)
    );
  },

  // Financial Reporting with condo filtering
  getQuarterlyReport: (quarter, year, condoId?: string) => {
    const { expenses, revenues, payments } = get();

    // Filter data by condo if specified
    const filteredExpenses = condoId ? expenses.filter(expense => expense.condoId === condoId) : expenses;
    const filteredRevenues = condoId ? revenues.filter(revenue => revenue.condoId === condoId) : revenues;
    const filteredPayments = condoId ? payments.filter(payment => payment.condoId === condoId) : payments;

    const quarterStart = new Date(year, (quarter - 1) * 3, 1);
    const quarterEnd = dateUtils.endOfQuarter(quarterStart);

    const quarterRevenues = filteredRevenues.filter(revenue =>
      dateUtils.isWithinInterval(new Date(revenue.date), { start: quarterStart, end: quarterEnd })
    );
    const totalRevenue = quarterRevenues.reduce((sum, revenue) => sum + revenue.amount, 0);

    const quarterExpenses = filteredExpenses.filter(expense =>
      dateUtils.isWithinInterval(new Date(expense.date), { start: quarterStart, end: quarterEnd })
    );
    const totalExpenses = quarterExpenses.reduce((sum, expense) => sum + expense.amount, 0);

    const quarterPayments = filteredPayments.filter(payment =>
      dateUtils.isWithinInterval(new Date(payment.paymentDate), { start: quarterStart, end: quarterEnd }) &&
      payment.status === 'completed'
    );
    const collectedAmount = quarterPayments.reduce((sum, payment) => sum + payment.amount, 0);

    // R10-5 — the `revenues` collection is a mock-era vestige: the backend has
    // no /api/revenues endpoint (nor a condo.revenues table), so it is always
    // empty and every revenue figure derived from it read $0. When no revenue
    // rows exist for the window, fall back to the REAL ledger — completed
    // payments — the same source the Atrium manager home (M-1) uses.
    const usePaymentsLedger = quarterRevenues.length === 0;
    const effectiveRevenue = usePaymentsLedger ? collectedAmount : totalRevenue;

    // S5.6: Accurate collection rate — paid fees / total fees × 100
    const feeCollectionRate = (() => {
      const condoFees = condoId
        ? get().fees.filter(f => f.condoId === condoId)
        : get().fees;
      const totalFeesSum = condoFees.reduce((s, f) => s + (f.amount ?? 0), 0);
      const paidFeesSum  = condoFees
        .filter(f => ['PAID', 'paid'].includes(f.status ?? ''))
        .reduce((s, f) => s + (f.amount ?? 0), 0);
      return totalFeesSum > 0 ? Math.round((paidFeesSum / totalFeesSum) * 100) : 0;
    })();

    const expensesByCategory = quarterExpenses.reduce((acc, expense) => {
      const categoryName = expense.category.charAt(0).toUpperCase() + expense.category.slice(1);
      const existing = acc.find(item => item.category === categoryName);
      if (existing) {
        existing.amount += expense.amount;
      } else {
        acc.push({ category: categoryName, amount: expense.amount });
      }
      return acc;
    }, [] as { category: string; amount: number }[]);

    const revenueBySource = quarterRevenues.reduce((acc, revenue) => {
      const sourceName = revenue.source.charAt(0).toUpperCase() + revenue.source.slice(1);
      const existing = acc.find(item => item.source === sourceName);
      if (existing) {
        existing.amount += revenue.amount;
      } else {
        acc.push({ source: sourceName, amount: revenue.amount });
      }
      return acc;
    }, [] as { source: string; amount: number }[]);

    // R10-5 fallback — group the quarter's collected payments by method so the
    // "Revenue Sources" breakdown reflects real ledger data instead of an
    // empty chart (labels: 'credit-card' → 'Credit Card').
    const revenueBySourceEffective = (usePaymentsLedger && quarterPayments.length > 0)
      ? Object.entries(
          quarterPayments.reduce((acc, p) => {
            const label = (p.method ?? 'other').split('-')
              .map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
            acc[label] = (acc[label] ?? 0) + p.amount;
            return acc;
          }, {} as Record<string, number>)
        ).map(([source, amount]) => ({ source, amount }))
      : revenueBySource;

    return {
      quarter,
      year,
      totalRevenue: effectiveRevenue,
      totalExpenses,
      netIncome: effectiveRevenue - totalExpenses,
      feeCollectionRate,
      totalFeesAmount: effectiveRevenue,
      collectedAmount,
      expensesByCategory,
      revenueBySource: revenueBySourceEffective,
      period: { start: quarterStart, end: quarterEnd }
    };
  },

  getCurrentQuarterReport: (condoId?: string) => {
    const now = new Date();
    const currentQuarter = dateUtils.getCurrentQuarter();
    return get().getQuarterlyReport(currentQuarter, now.getFullYear(), condoId);
  },

  getYearToDateReport: (condoId?: string) => {
    const now = new Date();
    const yearStart = new Date(now.getFullYear(), 0, 1);

    const ytdRevenues = get().getRevenuesByPeriod(yearStart, now, condoId);
    const ytdExpenses = get().getExpensesByPeriod(yearStart, now, condoId);

    // R10-5 — fall back to collected payments when no revenue rows exist
    // (the revenues collection has no backend and is always empty).
    const totalRevenue = ytdRevenues.length > 0
      ? ytdRevenues.reduce((sum, revenue) => sum + revenue.amount, 0)
      : collectedPaymentsInPeriod(get().payments, yearStart, now, condoId);
    const totalExpenses = ytdExpenses.reduce((sum, expense) => sum + expense.amount, 0);

    return {
      revenue: totalRevenue,
      expenses: totalExpenses,
      net: totalRevenue - totalExpenses
    };
  },

  getMonthlyReport: (month, year, condoId?: string) => {
    const monthStart = new Date(year, month - 1, 1);
    const monthEnd = new Date(year, month, 0, 23, 59, 59, 999);

    const monthRevenues = get().getRevenuesByPeriod(monthStart, monthEnd, condoId);
    const monthExpenses = get().getExpensesByPeriod(monthStart, monthEnd, condoId);

    // R10-5 — same payments-ledger fallback as getYearToDateReport.
    const totalRevenue = monthRevenues.length > 0
      ? monthRevenues.reduce((sum, revenue) => sum + revenue.amount, 0)
      : collectedPaymentsInPeriod(get().payments, monthStart, monthEnd, condoId);
    const totalExpenses = monthExpenses.reduce((sum, expense) => sum + expense.amount, 0);

    return {
      revenue: totalRevenue,
      expenses: totalExpenses,
      net: totalRevenue - totalExpenses
    };
  },

  // Owner Portal
  getOwnerDashboard: (ownerId, condoId) => {
    const { fees, getOwnerPayments, getOwnerDocuments } = get();

    // Sprint 36 — finding H2. "Pending" is the resident's mental
    // model for "fees I owe" — which must include OVERDUE, not just
    // status=PENDING. Filter expression now matches anything that
    // isn't PAID. Also stop matching null/undefined ownerId rows;
    // those would leak fees from other owners with un-linked
    // accounts into Sarah's "Pending" tile.
    const pendingFees = fees.filter(fee =>
      fee.ownerId === ownerId && fee.status !== 'PAID'
    );

    const recentPayments = getOwnerPayments(ownerId).slice(0, 5);
    const recentStatements = get().getOwnerStatements(ownerId).slice(0, 3);

    // Sprint 36 — finding H3. The previous `getOwnerDocuments(ownerId)`
    // matched docs where the OWNER was the uploader — i.e. zero
    // results unless the owner uploaded their own. Real intent: show
    // PUBLIC documents in the active condo. Fall back to the legacy
    // selector when condoId isn't supplied so non-Portal callers
    // don't break.
    const availableDocuments = condoId
      ? get().getPublicDocuments(condoId)
      : getOwnerDocuments(ownerId);

    const currentBalance = get().getOwnerBalance(ownerId);

    return {
      currentBalance,
      pendingFees,
      recentPayments,
      recentStatements,
      availableDocuments,
      owner: get().getOwnerById(ownerId)
    };
  },

  getOwnerBalance: (ownerId) => {
    // Sprint 36 — finding H1/H5. Drop the `null/undefined ownerId`
    // disjunction that used to swallow orphan-fee rows (and produce
    // an inflated balance that didn't tie out to the owner's actual
    // ledger). Now sums only fees explicitly owned by this resident
    // that aren't yet PAID.
    // Round-8 BUG-2: the balance is simply the sum of this owner's UNPAID
    // fees. A fee that has been paid flips to status PAID (and is excluded
    // here), and Sprint 108 now writes a real condo.payments row for each
    // settlement — so subtracting completed payments on top double-counts
    // every payment (the error == Σ completed payments, growing with history,
    // which is exactly the under-stated hero we saw live). Payment Centre
    // already sums unpaid fees only and was correct; match it here.
    const ownerFees = get().fees.filter(fee =>
      fee.ownerId === ownerId && fee.status !== 'PAID'
    );

    return ownerFees.reduce((sum, fee) => sum + fee.amount, 0);
  },

  // Unit Statistics
  getUnitStats: (condoId?: string) => {
    const { units } = get();
    const filteredUnits = condoId ? units.filter(u => u.condoId === condoId) : units;

    const totalUnits = filteredUnits.length;
    const occupiedUnits = filteredUnits.filter(u => u.status === 'occupied').length;
    const vacantUnits = filteredUnits.filter(u => u.status === 'available').length;
    const maintenanceUnits = filteredUnits.filter(u => u.status === 'maintenance').length;

    const occupancyRate = totalUnits > 0 ? (occupiedUnits / totalUnits) * 100 : 0;

    // Units by floor
    const unitsByFloor = filteredUnits.reduce((acc, unit) => {
      const floorIndex = acc.findIndex(f => f.floor === unit.floor);
      if (floorIndex >= 0) {
        acc[floorIndex].count++;
        if (unit.status === 'occupied') {
          acc[floorIndex].occupied++;
        }
      } else {
        acc.push({
          floor: unit.floor,
          count: 1,
          occupied: unit.status === 'occupied' ? 1 : 0
        });
      }
      return acc;
    }, [] as { floor: number; count: number; occupied: number }[]);

    // Units by bedrooms
    const unitsByBedrooms = filteredUnits.reduce((acc, unit) => {
      const bedroomIndex = acc.findIndex(b => b.bedrooms === unit.bedrooms);
      if (bedroomIndex >= 0) {
        acc[bedroomIndex].count++;
        if (unit.status === 'occupied') {
          acc[bedroomIndex].occupied++;
        }
      } else {
        acc.push({
          bedrooms: unit.bedrooms,
          count: 1,
          occupied: unit.status === 'occupied' ? 1 : 0
        });
      }
      return acc;
    }, [] as { bedrooms: number; count: number; occupied: number }[]);

    return {
      totalUnits,
      occupiedUnits,
      vacantUnits,
      maintenanceUnits,
      occupancyRate,
      unitsByFloor: unitsByFloor.sort((a, b) => a.floor - b.floor),
      unitsByBedrooms: unitsByBedrooms.sort((a, b) => a.bedrooms - b.bedrooms)
    };
  },
});
